import moment from "moment-timezone";

import * as companySessionSelectors from "@/components/new/CompanySession/company-session.selectors";
import * as userSessionSelectors from "@/components/UserSession/user-session.selectors";
import store from "@/redux/store";

// Every shape a Start/Finish time has ever been held in on the time entry forms.
//
// The time picker hands back "HH:mm:ss" and the forms are seeded with "HH:mm". "hh:mm A"
// is the old display format, and a Date is what the previous picker wrote into the form.
// We still accept the old two so that a stale value can never silently turn into "no time
// at all" - see parseFormTime.
export const FORM_TIME_FORMATS = ["HH:mm:ss", "HH:mm", "hh:mm A"];

// Read a Start/Finish time off the form.
//
// Do not replace this with a plain moment(value). moment("16:30:00") is NOT valid - it is
// not a full date, so moment falls back to new Date() and gives Invalid Date. That is how
// a saved time entry ended up with no start or finish time at all, silently recorded as an
// untimed entry instead.
export const parseFormTime = (value) =>
  value instanceof Date ? moment(value) : moment(value, FORM_TIME_FORMATS);

export const isDST = (timezone = "") => {
  const state = store.getState();
  const user = userSessionSelectors.getCurrentUser(state);

  return moment.tz(timezone ? timezone : (user?.timeZone ?? moment.tz.guess())).isDST();
};

export const getCurrentUserTimezone = () => {
  const state = store.getState();
  const user = userSessionSelectors.getCurrentUser(state);

  return user?.timeZone ?? moment.tz.guess();
};

export const getCurrentCompanyTimezone = () => {
  const state = store.getState();
  const company = companySessionSelectors.getCurrentCompany(state);

  return company?.timeZone ?? "";
};

export const toLocalDateTime = ({ date, timezone, format = "" }) => {
  if (!date) {
    return null;
  }

  const dateTime = moment.tz(date, timezone);

  return format !== "" ? dateTime.format(format) : dateTime.format();
};

export const toUtcDateTime = ({ date, timezone, format = "" }) => {
  if (!date) {
    return null;
  }

  const dateTime = moment.tz(date, timezone).utc();
  return format !== "" ? dateTime.format(format) : dateTime.format();
};

export const toUtcDateTimeToday = ({ format = "YYYY-MM-DDTHH:mm:ss[Z]" } = {}) => {
  const dateTime = moment.utc();

  return format !== "" ? dateTime.format(format) : dateTime.format();
};

export const toLocalDateTimeToday = ({
  format = "YYYY-MM-DDTHH:mm:ssZ",
  timezone = "UTC",
} = {}) => {
  return moment.tz(timezone).format(format);
};

export const toNaiveDateAndLocalize = ({ date, timezone }) => {
  const dateString = date.split("+")?.[0] || date;
  const [datePart, timePart] = dateString.split("T");
  const dateTime = new Date(`${datePart} ${timePart}`);

  return toLocalDateTime({ date: dateTime, timezone });
};

// Reads the wall time as written (the browser builds it in its own timezone) and places it in the
// company timezone. Taking the offset from the browser's own moment instead put times near a
// daylight-saving change an hour out whenever the browser and the company were in different zones.
export const toNaiveDateAndUTC = ({ date, timezone }) =>
  toUtcDateTime({ date: moment(date).format("YYYY-MM-DDTHH:mm:ss"), timezone });

// FAC-5618: edit forms hold a single Date plus time-of-day values seeded from the stored
// timestamps, so rebuilding datetimes from them loses the finish date (cross-midnight
// entries) and seconds. When none of the When fields changed, the caller must send the
// stored timestamps back verbatim instead of rebuilding them.
export const isTimeEntryWhenUntouched = ({
  dateValue,
  clockOnValue,
  clockOffValue,
  storedClockOn,
  storedClockOff,
  timezone,
  dateFormat,
}) => {
  const formatValue = (value, format) =>
    typeof value === "string" ? value : moment(value).format(format);

  // Compare the Start/Finish times to the minute, never to the second.
  //
  // The time picker can only ever pick a whole minute, so it hands back times ending in
  // ":00". Stored entries can carry real seconds (22:36:14). If we compared the two
  // directly, just opening the picker and closing it again without changing anything
  // would look like an edit, and saving would quietly overwrite 22:36:14 with 22:36:00.
  // Trimming both sides to HH:mm keeps "open and close" a no-op, which is what it looks
  // like to the person using it.
  const toMinute = (value) => {
    const parsed = value instanceof Date ? moment(value) : moment(value, FORM_TIME_FORMATS);
    return parsed.isValid() ? parsed.format("HH:mm") : "";
  };

  const initialDate = toLocalDateTime({ date: storedClockOn, timezone, format: dateFormat });
  const initialClockOn = toLocalDateTime({ date: storedClockOn, timezone, format: "HH:mm" });
  const initialClockOff = toLocalDateTime({ date: storedClockOff, timezone, format: "HH:mm" });

  return (
    formatValue(dateValue, dateFormat) === initialDate &&
    toMinute(clockOnValue) === initialClockOn &&
    toMinute(clockOffValue) === initialClockOff
  );
};

// FAC-5618: time entry forms hold one Date field plus time-only Start/Finish values.
// When the finish time-of-day is before the start time-of-day the entry crosses
// midnight, so the finish belongs to the NEXT calendar day. Returns the date string
// ("YYYY-MM-DD") the clock-off time must be combined with.
export const getClockOffDateValue = ({ dateValue, clockOn, clockOff }) => {
  const startTime = moment(clockOn, "HH:mm:ss", true);
  const finishTime = moment(clockOff, "HH:mm:ss", true);

  if (!startTime.isValid() || !finishTime.isValid() || !finishTime.isBefore(startTime)) {
    return dateValue;
  }

  return moment(dateValue, "YYYY-MM-DD").add(1, "day").format("YYYY-MM-DD");
};

const getCompanyDateSetting = () => {
  const state = store.getState();
  return companySessionSelectors.getCustomerDateFormatSetting(state);
};

const DATE_FORMAT_MAP = {
  "DD/MM/YYYY": "DD/MM/YYYY",
  "MM/DD/YYYY": "MM/DD/YYYY",
};

const DATE_FORMAT_SHORT_MAP = {
  "DD/MM/YYYY": "DD/MM/YYYY", //"DD MMM YYYY",
  "MM/DD/YYYY": "MM/DD/YYYY", //"MMM DD, YYYY",
};

const DATE_FORMAT_2DIGIT_MAP = {
  "DD/MM/YYYY": "DD/MM/YYYY", //"DD/MM/YY",
  "MM/DD/YYYY": "MM/DD/YYYY", //"MM/DD/YY",
};

const DATE_FORMAT_DAY_NAME_MAP = {
  "DD/MM/YYYY": "DD/MM/YYYY (dddd)",
  "MM/DD/YYYY": "MM/DD/YYYY (dddd)",
};

const DATE_FORMAT_MIDDLE_MAP = {
  "DD/MM/YYYY": "hh[:]mma DD/MM/YYYY", //"hh[:]mma DD/MM/YY",
  "MM/DD/YYYY": "hh[:]mma MM/DD/YYYY", //"hh[:]mma MM/DD/YY",
};

const DATE_FORMAT_FULL_CUSTOM_MAP = {
  "DD/MM/YYYY": "hh[:]mma dddd DD/MM/YYYY", //"hh[:]mma dddd DD/MM/YY",
  "MM/DD/YYYY": "hh[:]mma dddd MM/DD/YYYY", //"hh[:]mma dddd MM/DD/YY",
};

const DATE_FORMAT_FULL_MAP = {
  "DD/MM/YYYY": "hh[:]mma DD/MM/YYYY", //"hh[:]mma DD MMM YYYY",
  "MM/DD/YYYY": "hh[:]mma MM/DD/YYYY", //"hh[:]mma MMM DD, YYYY",
};

const DATE_FORMAT_SPECIAL_MAP = {
  "DD/MM/YYYY": "DD/MM/YYYY", //"Do MMMM YYYY",
  "MM/DD/YYYY": "MM/DD/YYYY", //"MMMM Do, YYYY",
};

const DATE_FORMAT_LONG_MAP = {
  "DD/MM/YYYY": "DD/MM/YYYY", //"DD MMMM YYYY",
  "MM/DD/YYYY": "MM/DD/YYYY", //"MMMM DD, YYYY",
};

const DATE_FORMAT_CHART_LABEL_MAP = {
  "DD/MM/YYYY": "DD MMM",
  "MM/DD/YYYY": "MMM DD",
};

export const getCompanyDateFormat = () => {
  return DATE_FORMAT_MAP[getCompanyDateSetting()] || "DD/MM/YYYY";
};

export const getCompanyDateFormatShort = () => {
  return DATE_FORMAT_SHORT_MAP[getCompanyDateSetting()] || "DD/MM/YYYY";
};

export const getCompanyDateFormat2Digit = () => {
  return DATE_FORMAT_2DIGIT_MAP[getCompanyDateSetting()] || "DD/MM/YYYY";
};

export const getCompanyDateFormatDayName = () => {
  return DATE_FORMAT_DAY_NAME_MAP[getCompanyDateSetting()] || "DD/MM/YYYY (dddd)";
};

export const getCompanyDateFormatMiddle = () => {
  return DATE_FORMAT_MIDDLE_MAP[getCompanyDateSetting()] || "hh[:]mma DD/MM/YYYY";
};

export const getCompanyDateFormatFullCustom = () => {
  return DATE_FORMAT_FULL_CUSTOM_MAP[getCompanyDateSetting()] || "hh[:]mma dddd DD/MM/YYYY";
};

export const getCompanyDateFormatFull = () => {
  return DATE_FORMAT_FULL_MAP[getCompanyDateSetting()] || "hh[:]mma DD/MM/YYYY";
};

export const getCompanyDateFormatSpecial = () => {
  return DATE_FORMAT_SPECIAL_MAP[getCompanyDateSetting()] || "DD/MM/YYYY";
};

export const getCompanyDateFormatLong = () => {
  return DATE_FORMAT_LONG_MAP[getCompanyDateSetting()] || "DD/MM/YYYY";
};

export const getCompanyDateFormatChartLabel = () => {
  return DATE_FORMAT_CHART_LABEL_MAP[getCompanyDateSetting()] || "DD MMM";
};

// R3 marker for a clock time listed under a "YYYY-MM-DD" day: "(next day)" for the day after,
// a short date for any other day. Compare each time with the row's day, never finish with start:
// a viewer in another timezone can see the start on a different date too.
export const getDayMarker = ({ time, rowDate, timezone }) => {
  if (!time || !rowDate) {
    return "";
  }

  const local = moment.tz(time, timezone);
  const localDate = local.format("YYYY-MM-DD");
  if (localDate === rowDate) {
    return "";
  }

  if (localDate === moment(rowDate, "YYYY-MM-DD").add(1, "day").format("YYYY-MM-DD")) {
    return "(next day)";
  }

  return `(${local.format(`ddd ${getCompanyDateFormatChartLabel()}`)})`;
};

// The planner keeps its values as plain "YYYY-MM-DDTHH:mm:ss" strings on the company clock. These
// two read one half out, and both return a string — not a Date.
// Both slices are bounded, so anything trailing the clock reading is dropped rather than passed on:
// "2026-06-22T09:00:00+10:00" and "2026-06-22T09:00:00Z" both give "09:00:00".
export const getDateWithoutTime = (value) => (value ? value.slice(0, 10) : null);

export const getTimeWithoutDate = (value) => (value ? value.slice(11, 19) : null);

export default {};
